import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTP, HTTPResponse } from '@ionic-native/http/ngx';
import { Platform } from '@ionic/angular';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { APP_CONFIG, SettingsService } from 'src/app/services/settings.service';
import EncodeUtils from 'src/app/utility/encode-utils';
import { xml2json } from 'src/app/utility/xml-utils';
import {
  CheckExtCommand,
  DocumentResponse,
  LastShiftTotalsCommand,
  LastShiftTotalsResponse,
  ServerStateCommand,
  ServerStateResponse,
  ShiftDocumentsCommand,
  ShiftDocumentsResponse,
  ShiftsCommand,
  ShiftsResponse,
  TaxDocumentResponseType,
  TaxError,
  TransactionsRegistrarStateCommand,
  TransactionsRegistrarStateResponse,
  ZRepExtCommand,
} from '../models/tax.model';
import { AgentSigner } from './agent-signer.service';
import { SharedService } from 'src/app/services/shared.service';
import { ApiV1Service } from 'src/app/services/api-v1.service';

export interface ITaxResponse {
  data?: any;
  error?: string;
  errorCode?: number;
  httpStatusCode?: number;
  success: boolean;
  status?: string;
}

@Injectable({ providedIn: 'root' })
export class TaxApiService {
  private _http: HttpClient;
  private _taxApiEndpoint: string;
  private get _testTaxApiRequestFailed() {
    const res = localStorage.getItem('_testTaxApiRequestFailed__');
    return res && res == 'true';
  }
  private set _testTaxApiRequestFailed(val: boolean) {
    localStorage.setItem('_testTaxApiRequestFailed__', val?.toString());
  }

  private _testTaxError: ITaxResponse = { success: false };
  private get _throwMockError() {
    const testTaxServer = this.settings.getValue(APP_CONFIG.TEST_TAX_SERVER);
    return testTaxServer && this._testTaxApiRequestFailed;
  }

  private get _testTaxServer() {
    const testTaxServer = this.settings.getValue(APP_CONFIG.TEST_TAX_SERVER);
    return testTaxServer;
  }

  constructor(
    private _httpBackend: HttpBackend,
    private _platform: Platform,
    private settings: SettingsService,
    private _nativeHttp: HTTP,
    private agentSigner: AgentSigner,
    private httpClient: HttpClient,
    private shared: SharedService,
    private _api: ApiV1Service
  ) {
    this._http = new HttpClient(this._httpBackend);
    this._taxApiEndpoint = this.settings.getValue(APP_CONFIG.TAX_API_URL);
    this.settings._getValue<string>(APP_CONFIG.TAX_API_URL).then((res) => {
      this._taxApiEndpoint = res;
    });
  }

  async getServerState() {
    if (this._throwMockError) {
      throw 'ServerState error.';
    }

    const command = new ServerStateCommand().toString();
    const serverState = await this._sendCommandToTax<ServerStateResponse>(
      btoa(command),
    );

    if (!serverState || !serverState.Timestamp) {
      throw 'ServerState. Сервер податкової недоступний';
    }

    return serverState;
  }

  async getStoreConfig(companyId: string, storeId: number): Promise<any[]> {
    const posApiUrl = await this.settings._getValue<string>(
      APP_CONFIG.POS_API_ENDPOINT
    );
    return this._http
      .get<any[]>(`${posApiUrl}/tax/storeConfigs/${storeId}`, {
        params: new HttpParams().set('responseFormater', 'none'),
        headers: new HttpHeaders().set('companyId', companyId),
      })
      .toPromise();
  }

  async getStorePrroConfigs(
    companyId: string,
    storeId: number,
  ): Promise<any[]> {
    const posApiUrl = await this.settings._getValue<string>(
      APP_CONFIG.POS_API_ENDPOINT
    );
    return this._http
      .get<any[]>(`${posApiUrl}/tax/storePrroConfigs/${storeId}`, {
        params: new HttpParams().set('responseFormater', 'none'),
        headers: new HttpHeaders().set('companyId', companyId),
      })
      .toPromise();
  }

  async ctxGetLastShiftTotals(
    contextKey: string,
    taxRegisterFiscalNUmber: string
  ): Promise<LastShiftTotalsResponse> {
    const command = new LastShiftTotalsCommand(taxRegisterFiscalNUmber);
    const signedData = await this.agentSigner.ctxSignData(
      contextKey,
      command.toString()
    );
    return this.sendCommandToTax<LastShiftTotalsResponse>(signedData);
  }

  async ctxGetTaxRegisterState(
    contextKey: string,
    taxRegisterFiscalNumber: string,
    offlineSessionId: string = null,
    offlineSeed: string = null,
    includeTaxObject = true
  ): Promise<TransactionsRegistrarStateResponse> {
    const command = new TransactionsRegistrarStateCommand(
      taxRegisterFiscalNumber,
      offlineSessionId,
      offlineSeed,
      includeTaxObject
    );

    const signedData = await this.agentSigner.ctxSignData(
      contextKey,
      command.toString()
    );

    const state =
      await this.sendCommandToTax<TransactionsRegistrarStateResponse>(
        signedData
      );

    return state;
  }

  async ctxGetShifts(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    from: Date,
    to: Date
  ): Promise<ShiftsResponse> {
    const command = new ShiftsCommand(taxRegisterFiscalNumber, from, to);
    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const shifts = await this._sendCommandToTax<ShiftsResponse>(
      signedData
    ).catch((err) => {
      const taxError = new TaxError(err.error);
      this._displayError(taxError.text, 10000);
      return null;
    });

    return shifts;
  }

  async ctxGetZRepExt(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    documentFiscalNum: string,
    responseType: TaxDocumentResponseType
  ): Promise<string> {
    const command = new ZRepExtCommand(
      taxRegisterFiscalNumber,
      documentFiscalNum,
      responseType
    );

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const zRepResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    ).catch((err) => {
      const taxError = new TaxError(err.error);
      this._displayError(taxError.text, 10000);
      return null;
    });

    return EncodeUtils.base64DecodeUnicode(zRepResponse.Data);
  }

  async ctxGetZRepJson(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    documentFiscalNum: string
  ): Promise<string> {
    const command = new ZRepExtCommand(
      taxRegisterFiscalNumber,
      documentFiscalNum,
      'OriginalXml'
    );

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const zRepResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    ).catch((err) => {
      const taxError = new TaxError(err.error);
      this._displayError(taxError.text, 10000);
      return null;
    });

    const win1251decoder = new TextDecoder('windows-1251');
    const arrayBuffer = EncodeUtils.base64ToArrayBuffer(zRepResponse.Data);
    const unicodeString = win1251decoder.decode(arrayBuffer);
    return unicodeString;
  }

  async ctxGetZRepXml(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    documentFiscalNum: string
  ): Promise<string> {
    const command = new ZRepExtCommand(
      taxRegisterFiscalNumber,
      documentFiscalNum,
      'OriginalXml'
    );

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const zRepResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    );

    return EncodeUtils.base64DecodeUnicode(atob(zRepResponse.Data));
  }

  async ctxGetShiftDocuments(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    shiftId: number,
    openShiftFiscalNum: number
  ): Promise<ShiftDocumentsResponse> {
    const command = new ShiftDocumentsCommand(
      taxRegisterFiscalNumber,
      shiftId,
      openShiftFiscalNum
    );

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const shiftDocumntsResponse =
      await this._sendCommandToTax<ShiftDocumentsResponse>(signedData).catch(
        (err) => {
          const taxError = new TaxError(err.error);
          this._displayError(taxError.text, 10000);
          return null;
        }
      );

    return shiftDocumntsResponse;
  }

  async ctxGetCheckExt(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    documentFiscalNum: string,
    responseType: TaxDocumentResponseType
  ): Promise<string> {
    const command = new CheckExtCommand({
      registrarNumFiscal: taxRegisterFiscalNumber,
      numFiscal: documentFiscalNum,
      type: responseType,
      acquireCabinetUrl: true,
    });

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const сheckExtResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    ).catch((err) => {
      const taxError = new TaxError(err.error);
      this._displayError(taxError.text, 10000);
      return null;
    });

    // const response = JSON.parse(сheckExtResponse) as DocumentResponse;
    return EncodeUtils.base64DecodeUnicode(сheckExtResponse.Data);
  }

  async ctxGetCheckXml(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    documentFiscalNum: string
  ): Promise<string> {
    const command = new CheckExtCommand({
      registrarNumFiscal: taxRegisterFiscalNumber,
      numFiscal: documentFiscalNum,
      type: 'OriginalXml',
      acquireCabinetUrl: true,
    });

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const сheckXmlResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    );

    const data = EncodeUtils.base64ToArrayBuffer(сheckXmlResponse.Data);
    const win1251decoder = new TextDecoder('windows-1251');
    return win1251decoder.decode(data);
  }

  async ctxGetCheckXmlByLocalNum(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    numLocal: number
  ): Promise<string | null> {
    const command = new CheckExtCommand({
      registrarNumFiscal: taxRegisterFiscalNumber,
      numLocal,
      type: 'OriginalXml',
      acquireCabinetUrl: false,
    });

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const checkXmlResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    ).catch(() => null);

    if (!checkXmlResponse?.Data) {
      return null;
    }

    const data = EncodeUtils.base64ToArrayBuffer(checkXmlResponse.Data);
    const win1251decoder = new TextDecoder('windows-1251');
    return win1251decoder.decode(data);
  }

  // The original XML of a Z-report by its local number, or null.
  async ctxGetZRepXmlByLocalNum(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    numLocal: number
  ): Promise<string | null> {
    const command = new ZRepExtCommand(
      taxRegisterFiscalNumber,
      null,
      'OriginalXml',
      numLocal
    );

    const signedData = await this.agentSigner.ctxSignData(
      ctxKey,
      command.toString()
    );
    const zRepResponse = await this._sendCommandToTax<DocumentResponse>(
      signedData
    ).catch(() => null);

    if (!zRepResponse?.Data) {
      return null;
    }

    const data = EncodeUtils.base64ToArrayBuffer(zRepResponse.Data);
    return new TextDecoder('windows-1251').decode(data);
  }

  // A server document by its local number: a check, else a Z-report. The bytes are kept as
  // received, for the next document's PREVDOCHASH. type: OriginalXml (the server's copy of the
  // XML), or SignedBySenderXml (the package the register signed and sent). Null when the server
  // has neither.
  async ctxGetDocumentByLocalNum(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    numLocal: number,
    type: TaxDocumentResponseType = 'OriginalXml'
  ): Promise<{ xml: string; bytes: Uint8Array } | null> {
    const commands = [
      new CheckExtCommand({
        registrarNumFiscal: taxRegisterFiscalNumber,
        numLocal,
        type,
        acquireCabinetUrl: false,
      }),
      new ZRepExtCommand(taxRegisterFiscalNumber, null, type, numLocal),
    ];
    for (const command of commands) {
      const signedData = await this.agentSigner.ctxSignData(
        ctxKey,
        command.toString()
      );
      const response = await this._sendCommandToTax<DocumentResponse>(
        signedData
      ).catch(() => null);
      if (response?.Data) {
        const bytes = new Uint8Array(
          EncodeUtils.base64ToArrayBuffer(response.Data)
        );
        return { xml: new TextDecoder('windows-1251').decode(bytes), bytes };
      }
    }
    return null;
  }

  async sendCommandToTax<T>(signedDocument: string | Uint8Array): Promise<T> {
    return this._sendCommandToTax<T>(signedDocument);
  }

  private async _sendCommandToTax<T>(
    signedDocument: string | Uint8Array
  ): Promise<T> {
    // this._logService.info(this._serviceName, '_sendCommandToTax');
    const signedDocBytes = EncodeUtils.base64ToArrayBuffer(
      <string>signedDocument
    );
    const headers = {
      'Content-Type': 'application/octet-stream',
    };

    const apiTaxUrl = this.settings.getValue(APP_CONFIG.TAX_API_URL);
    const platform = await this._platform.ready();
    if (platform == 'dom') {
      return this._http
        .post<any>(`${apiTaxUrl}/tax/cmd`, {
          SignedData: signedDocument,
        })
        .toPromise()
        .then((res) => {
          if (res.success) {
            return <T>res.data;
          }

          if (res.error) {
            throw res.error;
          }

          throw res;
        })
        .catch((err) => {
          this._api
            .prroLog('Error', JSON.stringify(err), '', {
              method: 'cmd',
            })
            .subscribe();
          throw err;
        });
    } else {
      const response = await this._sendRequest(
        this.settings.fs_tax_cmd,
        {
          method: 'post',
          data: signedDocBytes,
          headers: headers,
          responseType: 'json',
        },
        'raw'
      ).catch((err) => {
        this._api
          .prroLog('Error', JSON.stringify(err), '', {
            method: 'cmd',
          })
          .subscribe();
        throw err;
      });

      return response.data;
    }
  }

  async sendDocumentToTax(signedDocument: string): Promise<ITaxResponse> {
    if (this._throwMockError) {
      return this._testTaxError;
    }

    const signedDocBytes = EncodeUtils.base64ToArrayBuffer(signedDocument);
    const headers = {
      'Content-Type': 'application/octet-stream',
    };

    const apiTaxUrl = this.settings.getValue(APP_CONFIG.TAX_API_URL);
    // A server that does not answer in time is handled like a connection error: the sale goes
    // on offline (the setting tax_doc_timeout_seconds).
    const timeoutSeconds = this.settings.taxDocTimeoutSeconds;
    const platform = await this._platform.ready();
    if (platform == 'dom') {
      return this._http
        .post<ITaxResponse>(`${apiTaxUrl}/tax/doc`, {
          SignedData: signedDocument,
        })
        .pipe(timeout(timeoutSeconds * 1000))
        .toPromise()
        .then((res) => {
          if (this._testTaxServer) {
            this._testTaxApiRequestFailed = true;
            return this._testTaxError;
          }

          this._testTaxApiRequestFailed = null;

          if (!res.success) {
            this._api
              .prroLog('Error', JSON.stringify(res), '', {
                method: 'doc',
              })
              .subscribe();
          }
          return res;
        })
        .catch((err) => {
          if (err?.name === 'TimeoutError') {
            return this._handleDocTimeout(timeoutSeconds);
          }
          this._api
            .prroLog('Error', JSON.stringify(err || {}), '', { method: 'doc' })
            .subscribe();
          return this._handleHttpError(err);
        });
    } else {
      this._nativeHttp.setDataSerializer('raw');
      const result = await this._nativeHttp
        .sendRequest(this.settings.fs_tax_doc, {
          method: 'post',
          data: signedDocBytes,
          headers: headers,
          responseType: 'arraybuffer',
          timeout: timeoutSeconds,
        })
        .then((resp) => {
          if (this._testTaxServer) {
            this._testTaxApiRequestFailed = true;
            return this._testTaxError;
          }

          this._testTaxApiRequestFailed = null;
          const result: ITaxResponse = { success: false };
          result.data = EncodeUtils.arrayBufferToBase64(resp.data);
          result.success = true;

          return result;
        })
        .catch((err) => {
          // The plugin reports a timeout as status -4.
          if (err?.status === -4) {
            return this._handleDocTimeout(timeoutSeconds);
          }
          this._api
            .prroLog('Error', JSON.stringify(err || {}), '', { method: 'doc' })
            .subscribe();
          return this._handleHttpError(err);
        });

      return result;
    }
  }

  // No answer in time: not a rejection (no HTTP status), so the document goes on offline.
  private _handleDocTimeout(timeoutSeconds: number): ITaxResponse {
    const error = `Сервер податкової не відповів за ${timeoutSeconds} с.`;
    this._api.prroLog('Error', error, '', { method: 'doc' }).subscribe();
    return {
      success: false,
      httpStatusCode: 0,
      status: 'Timeout',
      error,
      errorCode: 1004,
    };
  }

  // "Запит надсилання пакета офлайн документів": the KEP-signed SendOflPackage JSON posted to
  // /cmd. The body is returned as received: "OK" for an ordinary package, JSON with the next
  // session's OfflineSessionId and OfflineSeed for the package with OfflineEnd.
  async sendOfflinePackageCommand(
    signedCommand: string,
  ): Promise<ITaxResponse> {
    if (this._throwMockError) {
      return this._testTaxError;
    }

    const apiTaxUrl = this.settings.getValue(APP_CONFIG.TAX_API_URL);
    const platform = await this._platform.ready();
    if (platform == 'dom') {
      return this._http
        .post<ITaxResponse>(`${apiTaxUrl}/tax/cmd`, {
          SignedData: signedCommand,
        })
        .toPromise()
        .then((res) => {
          if (this._testTaxServer) {
            this._testTaxApiRequestFailed = true;
            return this._testTaxError;
          }

          this._testTaxApiRequestFailed = null;
          return res;
        })
        .catch((err) => {
          this._api
            .prroLog('Error', JSON.stringify(err || {}), '', {
              method: 'SendOflPackage',
            })
            .subscribe();
          return this._handleHttpError(err);
        });
    }

    const signedCommandBytes = EncodeUtils.base64ToArrayBuffer(signedCommand);
    return this._sendRequest(
      this.settings.fs_tax_cmd,
      {
        method: 'post',
        data: signedCommandBytes,
        headers: { 'Content-Type': 'application/octet-stream' },
        responseType: 'text',
      },
      'raw',
    )
      .then((resp) => {
        if (this._testTaxServer) {
          this._testTaxApiRequestFailed = true;
          return this._testTaxError;
        }

        this._testTaxApiRequestFailed = null;
        const result: ITaxResponse = {
          success: true,
          data: resp.data,
          httpStatusCode: resp.status,
        };
        return result;
      })
      .catch((err) => {
        this._api
          .prroLog('Error', JSON.stringify(err || {}), '', {
            method: 'SendOflPackage',
          })
          .subscribe();
        return this._handleHttpError(err);
      });
  }

  async sendPackageToTax(signedPackege: string): Promise<ITaxResponse> {
    if (this._throwMockError) {
      return this._testTaxError;
    }

    const apiTaxUrl = this.settings.getValue(APP_CONFIG.TAX_API_URL);
    const headers = {
      'Content-Type': 'application/octet-stream',
    };

    const platform = await this._platform.ready();
    if (platform == 'dom') {
      return this._http
        .post<ITaxResponse>(`${apiTaxUrl}/tax/pck`, {
          SignedData: signedPackege,
        })
        .toPromise()
        .then((res) => {
          if (this._testTaxServer) {
            this._testTaxApiRequestFailed = true;
            return this._testTaxError;
          }

          this._testTaxApiRequestFailed = null;
          return res;
        })
        .catch((err) => {
          this._api
            .prroLog('Error', JSON.stringify(err || {}), '', { method: 'pck' })
            .subscribe();
          return this._handleHttpError(err);
        });
    } else {
      const signedDocBytes = EncodeUtils.base64ToArrayBuffer(signedPackege);
      this._nativeHttp.setDataSerializer('raw');
      const result = await this._nativeHttp
        .sendRequest(this.settings.fs_tax_pck, {
          method: 'post',
          data: signedDocBytes,
          headers: headers,
          responseType: 'arraybuffer',
        })
        .then((resp) => {
          if (this._testTaxServer) {
            this._testTaxApiRequestFailed = true;
            return this._testTaxError;
          }

          this._testTaxApiRequestFailed = null;
          const result: ITaxResponse = { success: false };
          result.data = EncodeUtils.arrayBufferToBase64(resp.data);
          result.success = true;

          return result;
        })
        .catch((err) => {
          this._api
            .prroLog('Error', JSON.stringify(err || {}), '', { method: 'pck' })
            .subscribe();

          return this._handleHttpError(err);
        });

      return result;
    }
  }

  private _handleHttpError(err: any) {
    const result: ITaxResponse = { success: false };
    if (err.status) {
      result.httpStatusCode = err.status;
      result.status = 'HttpRequestFailed';
      result.error = err.error;
      result.errorCode = 1002;
    } else {
      result.httpStatusCode = err.status;
      result.status = 'UnknownError';
      result.error = err.error;
      result.errorCode = 1003;
    }

    return result;
  }

  private _sendRequest(
    url: string,
    options: {
      method:
        | 'get'
        | 'post'
        | 'put'
        | 'patch'
        | 'head'
        | 'delete'
        | 'options'
        | 'upload'
        | 'download';
      data?: {
        [index: string]: any;
      };
      params?: {
        [index: string]: string | number;
      };
      serializer?: 'json' | 'urlencoded' | 'utf8' | 'multipart' | 'raw';
      timeout?: number;
      headers?: {
        [index: string]: string;
      };
      filePath?: string | string[];
      name?: string | string[];
      responseType?: 'text' | 'arraybuffer' | 'blob' | 'json';
    },
    serializer: 'urlencoded' | 'json' | 'utf8' | 'multipart' | 'raw'
  ): Promise<HTTPResponse> {
    this._nativeHttp.setDataSerializer(serializer);
    return this._nativeHttp
      .sendRequest(url, options)
      .finally(() => this._nativeHttp.setDataSerializer('json'));
  }

  private _displayError(message: string, duration = 3000) {
    if (!message) {
      return;
    }

    this.shared.showErrorToast(message, duration);
  }
}
